import type { AuditFile, TbRow } from "./india";
import {
  addQueryMessage,
  newQuery,
  querySchema,
  type AuditQuery,
  type QueryPriority,
} from "./queries";

/** Suggestions are screening prompts. They are never audit conclusions. */
export interface QuerySuggestion {
  id: string;
  kind:
    | "current-imbalance"
    | "prior-imbalance"
    | "unmapped"
    | "movement"
    | "new-balance";
  title: string;
  description: string;
  area: string;
  priority: QueryPriority;
  /** The balance/difference to investigate, not an established misstatement. */
  financialImpact: number;
  accountCodes: string[];
}

export interface SuggestionOptions {
  /** Positive absolute screening threshold; defaults to valid performance materiality. */
  movementThreshold?: number;
  /** Relative change threshold, as a percentage (25 means 25%). */
  movementPercent?: number;
}

interface Assignment {
  reason: string;
  owner: string;
  clientContact: string;
  dueDate: string;
}

const ORIGIN_PREFIX = "AuditFlow assistance origin: ";
const originLine = (key: string) => `${ORIGIN_PREFIX}${key}`;
const keyPart = (value: string) => encodeURIComponent(value);
const sourceKey = (source: AuditFile, queryId: string) =>
  `carry-forward:${keyPart(source.id)}:${keyPart(queryId)}`;

function hasOrigin(queries: AuditQuery[], key: string): boolean {
  return queries.some((query) =>
    query.messages.some(
      (message) =>
        message.visibility === "internal" &&
        message.text.split("\n")[0] === originLine(key),
    ),
  );
}

function validDay(value: string): boolean {
  return (
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    Number.isFinite(Date.parse(value)) &&
    new Date(value).toISOString().slice(0, 10) === value
  );
}

function reassessment(reason: string): string {
  const text = reason.trim();
  if (text.replace(/\s/g, "").length < 20)
    throw new Error(
      "Document a reassessment reason of at least 20 non-whitespace characters.",
    );
  return text;
}

function writable(file: AuditFile): void {
  if (file.stage === "Archived")
    throw new Error("Archived engagements cannot receive new queries.");
}

function sequence(queries: AuditQuery[]): number {
  return queries.reduce((highest, query) => {
    const match = /^Q-(\d+)$/.exec(query.reference);
    const value = match ? Number(match[1]) : 0;
    return Number.isSafeInteger(value) ? Math.max(highest, value) : highest;
  }, queries.length);
}

/** Nonempty registered client IDs are required; names alone are not identity. */
export function carryForwardEligibility(
  source: AuditFile,
  target: AuditFile,
): string[] {
  const errors: string[] = [];
  if (!source.id.trim() || !target.id.trim() || source.id === target.id)
    errors.push("Choose a different source engagement with a saved identity.");
  if (
    !source.clientId.trim() ||
    !target.clientId.trim() ||
    source.clientId !== target.clientId
  )
    errors.push(
      "Source and target must have the same registered client ID; client names are insufficient.",
    );
  if (source.framework !== target.framework)
    errors.push(
      "The accounting framework changed; create a fresh query after reassessing applicability.",
    );
  if (
    source.entity !== target.entity ||
    source.consolidated !== target.consolidated
  )
    errors.push(
      "Source and target must have the same entity type and standalone/consolidated scope.",
    );
  if (
    !validDay(source.periodEnd) ||
    !validDay(target.periodEnd) ||
    source.periodEnd >= target.periodEnd
  )
    errors.push(
      "The target period end must be valid and later than the source period end.",
    );
  if (target.stage === "Archived")
    errors.push("Archived engagements cannot receive carried-forward queries.");
  return errors;
}

/** A fresh current-period draft: prior evidence, replies and approvals are discarded. */
export function carryForwardQuery(
  input: Assignment & { source: AuditFile; target: AuditFile; queryId: string },
  actor: string,
): AuditQuery {
  const { source, target } = input;
  const errors = carryForwardEligibility(source, target);
  if (errors.length) throw new Error(errors.join(" "));
  const reason = reassessment(input.reason);
  const original = source.queries.find((query) => query.id === input.queryId);
  if (!original)
    throw new Error(
      "The source query does not belong to the selected engagement.",
    );
  querySchema.parse(original);
  const key = sourceKey(source, original.id);
  if (hasOrigin(target.queries, key))
    throw new Error(
      "This prior-period query has already been carried forward; review the existing draft or query.",
    );
  const draft = newQuery(
    {
      title: `Current-period follow-up: ${original.title}`,
      description: `Please provide current-period information and supporting records to reassess “${original.title}” for the period ended ${target.periodEnd}. Prior-period answers and conclusions have not been carried forward.`,
      area: original.area,
      owner: input.owner,
      clientContact: input.clientContact,
      dueDate: input.dueDate,
      priority: "Normal",
      financialImpact: 0,
      blocksCompletion: true,
    },
    actor,
    sequence(target.queries),
  );
  return addQueryMessage(
    draft,
    {
      visibility: "internal",
      text: `${originLine(key)}\nSource engagement: ${source.id}; period ended ${source.periodEnd}; ${source.framework}; source query ${original.reference}.\nCurrent-period reassessment: ${reason}\nPrior-period request, for context only: ${original.description}\nReset: status, assignment, due date, priority, financial impact, procedure link, responses, evidence and all approvals require current-period assessment.`,
    },
    actor,
  );
}

function validatedRows(rows: TbRow[]): TbRow[] {
  const codes = new Set<string>();
  for (const row of rows) {
    const code = row.code.trim();
    if (!code || !row.name.trim() || codes.has(code))
      throw new Error(
        "Trial balance account codes and names must be present; account codes must be unique.",
      );
    codes.add(code);
    if (
      ![row.current, row.prior].every(
        (value) =>
          Number.isFinite(value) &&
          Math.abs(value) < 1e12 &&
          Math.abs(value * 100 - Math.round(value * 100)) < 0.01,
      )
    )
      throw new Error(
        "Trial balance screening requires finite signed amounts below 1 trillion, with at most two decimals.",
      );
  }
  return rows;
}

const money = (value: number) =>
  value.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** Undefined means materiality is incomplete, so balance/movement screens are disabled. */
export function effectiveMovementThreshold(
  file: AuditFile,
  options: SuggestionOptions = {},
): number | undefined {
  if (options.movementThreshold !== undefined) {
    if (
      !Number.isFinite(options.movementThreshold) ||
      options.movementThreshold <= 0
    )
      throw new Error(
        "Movement screening threshold must be a positive finite amount.",
      );
    return options.movementThreshold;
  }
  const m = file.materiality;
  if (
    ![m.base, m.percent, m.performancePercent].every(Number.isFinite) ||
    m.base <= 0 ||
    m.percent <= 0 ||
    m.percent > 100 ||
    m.performancePercent <= 0 ||
    m.performancePercent >= 100 ||
    !m.rationale.trim()
  )
    return undefined;
  const overall = Math.round(m.base * m.percent) / 100;
  const threshold = Math.round(overall * m.performancePercent) / 100;
  return Number.isFinite(threshold) && threshold > 0 ? threshold : undefined;
}

/** Pure TB screening, with no assumed ledger, tax-return or bank matching. */
export function reconciliationSuggestions(
  file: AuditFile,
  options: SuggestionOptions = {},
): QuerySuggestion[] {
  const rows = validatedRows(file.tb);
  const threshold = effectiveMovementThreshold(file, options);
  const movementPercent = options.movementPercent ?? 25;
  if (!Number.isFinite(movementPercent) || movementPercent < 0)
    throw new Error(
      "Relative movement threshold must be a finite non-negative percentage.",
    );
  const result: QuerySuggestion[] = [];
  const add = (suggestion: Omit<QuerySuggestion, "id">, key: string) => {
    const id = `tb:${keyPart(file.id)}:${key}`;
    // Closed queries are also suppressed: reopen/reassess the existing query explicitly.
    if (!hasOrigin(file.queries, id)) result.push({ ...suggestion, id });
  };
  for (const period of ["current", "prior"] as const) {
    let cents = 0;
    for (const row of rows) {
      cents += Math.round(row[period] * 100);
      if (!Number.isSafeInteger(cents))
        throw new Error("Trial balance total exceeds safe calculation limits.");
    }
    const difference = cents / 100;
    if (cents !== 0)
      add(
        {
          kind: `${period}-imbalance`,
          title: `${period === "current" ? "Current" : "Prior"}-period trial balance does not balance`,
          description: `The imported ${period}-period signed trial balance has a net difference of ${money(difference)}. Reconcile the imported population to the source books and provide a balanced export or a supported explanation. This screening difference is not an established misstatement.`,
          area: "Trial balance",
          priority: "High",
          financialImpact: Math.abs(difference),
          accountCodes: [],
        },
        `${period}-imbalance`,
      );
  }
  for (const row of [...rows].sort((a, b) => a.code.localeCompare(b.code))) {
    const account = `${row.code} — ${row.name}`;
    if (!row.mapping.trim())
      add(
        {
          kind: "unmapped",
          title: `Confirm financial statement mapping: ${account}`,
          description: `Account ${account} has no financial statement mapping in the imported trial balance (current ${money(row.current)}, prior ${money(row.prior)}). Confirm the appropriate mapping and supporting rationale. This is a mapping exception, not an established misstatement.`,
          area: "Financial statements",
          priority: "Normal",
          financialImpact: Math.abs(row.current),
          accountCodes: [row.code],
        },
        `unmapped:${keyPart(row.code)}`,
      );
    if (threshold === undefined) continue;
    const delta = Math.round((row.current - row.prior) * 100) / 100;
    if (row.prior === 0 && Math.abs(row.current) >= threshold) {
      add(
        {
          kind: "new-balance",
          title: `Explain new account balance: ${account}`,
          description: `Account ${account} has a current balance of ${money(row.current)} and a nil prior-period balance, meeting the absolute screening threshold of ${money(threshold)}. Provide the composition and explanation, including whether the account is new or was remapped. This is an analytical prompt, not a finding.`,
          area: "Analytical review",
          priority: "Normal",
          financialImpact: Math.abs(row.current),
          accountCodes: [row.code],
        },
        `new-balance:${keyPart(row.code)}`,
      );
    } else if (
      row.prior !== 0 &&
      Math.abs(delta) >= threshold &&
      (Math.abs(delta) / Math.abs(row.prior)) * 100 >= movementPercent
    ) {
      const percent = (Math.abs(delta) / Math.abs(row.prior)) * 100;
      add(
        {
          kind: "movement",
          title: `Explain account movement: ${account}`,
          description: `Account ${account} changed from ${money(row.prior)} to ${money(row.current)}: signed movement ${money(delta)}, absolute relative movement ${percent.toFixed(1)}%. It meets the screening thresholds of ${money(threshold)} and ${movementPercent}%. Explain the movement and provide support, considering reclassification and comparability. This is an analytical prompt, not a finding.`,
          area: "Analytical review",
          priority: "Normal",
          financialImpact: Math.abs(delta),
          accountCodes: [row.code],
        },
        `movement:${keyPart(row.code)}`,
      );
    }
  }
  return result;
}

/** Human selection and a recorded rationale are required; stale suggestions are rejected. */
export function createQueryFromSuggestion(
  input: Assignment & {
    file: AuditFile;
    suggestionId: string;
    options?: SuggestionOptions;
  },
  actor: string,
): AuditQuery {
  writable(input.file);
  const reason = reassessment(input.reason);
  const suggestion = reconciliationSuggestions(input.file, input.options).find(
    (item) => item.id === input.suggestionId,
  );
  if (!suggestion)
    throw new Error(
      "This suggestion no longer applies or already has a query. Refresh the screening results.",
    );
  const draft = newQuery(
    {
      title: suggestion.title,
      description: suggestion.description,
      area: suggestion.area,
      priority: suggestion.priority,
      // The screening exposure is contextual, not an assessed query financial impact.
      financialImpact: 0,
      blocksCompletion: true,
      owner: input.owner,
      clientContact: input.clientContact,
      dueDate: input.dueDate,
    },
    actor,
    sequence(input.file.queries),
  );
  return addQueryMessage(
    draft,
    {
      visibility: "internal",
      text: `${originLine(suggestion.id)}\nScreened engagement ${input.file.id}, period ended ${input.file.periodEnd}; TB source: ${input.file.tbSource || "not recorded"}.\nScreening balance/difference: ${money(suggestion.financialImpact)}; this is not an assessed misstatement.\nAuditor decision to raise this draft: ${reason}\nOnly imported trial balance balances and mappings were screened. No ledger, bank or tax-return reconciliation was performed.`,
    },
    actor,
  );
}
